/**
 * discoveryResume — pure helpers for resuming a discovery session.
 *
 * v4.18 - DISCOVERY-LIFECYCLE F02b - 2026-09-15
 *
 * Deliberately free of axios and React so jest can import it directly. Every other portal
 * test in this repo is forced into mirror form because importing a component pulls
 * services/api.ts -> axios, which ships ESM that CRA's jest does not transform. A mirror can
 * silently disagree with the code it mirrors; these two functions are the real thing.
 */

export interface ResumeStartFields {
  started_at?: string | null;
}

/**
 * The Date the client clock should count from on resume.
 *
 * `started_at` is the server's own session start. It is used when it parses and is not in
 * the future; otherwise the clock starts now, which is the pre-v4.18 behaviour and the
 * correct fallback for an old API that does not send the field at all.
 */
export function resumeStartTime(open: ResumeStartFields | null | undefined, now: Date): Date {
  const raw = open?.started_at;
  if (typeof raw !== 'string' || raw.trim() === '') {
    return now;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return now;
  }
  if (parsed.getTime() > now.getTime()) {
    return now;
  }
  return parsed;
}

/**
 * Whole seconds between `start` and `now`, never negative.
 *
 * Set once at resume. The timer effect ticks only while startTime is set AND the review
 * screen is closed, so a session resumed straight into review would otherwise render a zero
 * duration however startTime was set.
 */
export function elapsedSecondsSince(start: Date, now: Date): number {
  const delta = now.getTime() - start.getTime();
  if (!Number.isFinite(delta) || delta <= 0) {
    return 0;
  }
  return Math.floor(delta / 1000);
}
